import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { Project, ProjectDocument, ProjectEvent } from "../types";
import { Fold } from "./Fold";
import { formatDay } from "./projectUtils";
import { useEventText } from "./useEventText";

/** Historique de la fiche : ce qui a changé, quand, et le document qui l'a appris. Les derniers changements sont
 * affichés, les plus anciens repliés (une fiche fusionnée en compte des dizaines). */
const RECENT = 3;

export const ProjectHistory = ({
  project,
  documents,
}: {
  project: Project;
  documents?: ProjectDocument[];
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { data: events, isPending } = useGetList<ProjectEvent>(
    "project_events",
    {
      filter: { project_id: project.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "occurred_at", order: "DESC" },
    },
  );
  const { label, change } = useEventText();
  if (isPending) return null;
  // Fonction de rendu, pas un composant : un composant déclaré ici serait recréé à chaque rendu.
  const renderEvents = (list: ProjectEvent[], className?: string) => (
    <ul className={cn("flex flex-col gap-3", className)}>
      {list.map((event) => {
        const source = documents?.find(
          (document) => document.id === event.document_id,
        );
        return (
          <li
            key={event.id}
            className="grid grid-cols-1 @md:grid-cols-[10rem_1fr] gap-x-4 text-sm"
          >
            <span className="text-muted-foreground">
              {formatDay(event.occurred_at, locale)}
            </span>
            <div>
              <div>
                <span className="font-medium">{label(event.field)}</span>
                {" : "}
                {change(event)}
              </div>
              {source && (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground underline"
                >
                  {source.title}
                </a>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );

  return (
    <Card>
      <CardContent>
        <h3 className="text-sm font-bold mb-4">
          {translate("resources.projects.history.title")}
        </h3>
        {!events?.length ? (
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.history.empty")}
          </p>
        ) : (
          <>
            {renderEvents(events.slice(0, RECENT))}
            {events.length > RECENT && (
              <Fold
                className="mt-3"
                summary={translate("resources.projects.history.older", {
                  smart_count: events.length - RECENT,
                })}
              >
                {renderEvents(events.slice(RECENT), "mt-3")}
              </Fold>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
